import { z } from 'zod';
import {
  EditorStarterAsset,
  VideoAsset,
} from '@/features/remotion-editor/editor/assets/assets';

// Shadow 스키마
const shadowSchema = z.object({
  color: z.string(),
  opacity: z.number(),
  visible: z.boolean(),
  offsetX: z.number(),
  offsetY: z.number(),
  blur: z.number(),
});

// Background 스키마 (텍스트 배경)
const backgroundSchema = z.object({
  color: z.string(),
  opacity: z.number(),
  visible: z.boolean(),
  horizontalPadding: z.number(),
  verticalPadding: z.number(),
  borderRadius: z.number(),
  mode: z.enum(['text-only', 'full-area']),
  verticalOffset: z.number(),
});

// FontStyle 스키마
const fontStyleSchema = z.object({
  variant: z.string(),
  weight: z.string(),
});

// Text 공통 속성 스키마 (title, caption)
const textStyleSchema = z.object({
  color: z.string(),
  width: z.number(),
  height: z.number(),
  align: z.string(),
  opacity: z.number(),
  rotation: z.number(),
  fontFamily: z.string(),
  fontSize: z.number(),
  lineHeight: z.number(),
  letterSpacing: z.number(),
  direction: z.string(),
  fontStyle: fontStyleSchema,
  strokeWidth: z.number(),
  strokeColor: z.string(),
  fadeInDurationInSeconds: z.number(),
  fadeOutDurationInSeconds: z.number(),
  xRatio: z.number(),
  yRatio: z.number(),
  shadow: shadowSchema.optional(),
  shadowVisible: z.boolean().optional(),
  background: backgroundSchema.optional(), // 텍스트 배경
  highlightColor: z.string().nullable().optional(), // caption에만 사용되는 하이라이트 색상 (null = 하이라이트 없음)
});

// Video 스타일 스키마
const videoStyleSchema = z.object({
  width: z.number(),
  height: z.number(),
  xScale: z.number(),
  yScale: z.number(),
  opacity: z.number(),
  borderRadius: z.number(),
  rotation: z.number(),
  keepAspectRatio: z.boolean(),
  fadeInDurationInSeconds: z.number(),
  fadeOutDurationInSeconds: z.number(),
  xRatio: z.number(),
  yRatio: z.number(),
  videoAspectRatio: z.enum(['16:9', '9:16']).optional(),
  cropTop: z.number().default(0),
  cropLeft: z.number().default(0),
  cropRight: z.number().default(0),
  cropBottom: z.number().default(0),
});

// Asset 스타일 스키마 (공통 스타일 속성)
const baseAssetStyleSchema = z.object({
  assetId: z.string(), // 원본 asset의 ID 참조
  width: z.number(),
  height: z.number(),
  xRatio: z.number(),
  yRatio: z.number(),
  opacity: z.number(),
  rotation: z.number(),
  fadeInDurationInSeconds: z.number(),
  fadeOutDurationInSeconds: z.number(),
  trackOrder: z.number(), // tracks 배열에서의 순서 (CSS zIndex 아님)
  // 명시적으로 "맨 뒤로 보내기"한 경우만 true
  sendToBack: z.boolean().optional(),
  hidden: z.boolean().optional(),
});

// Image Asset 스타일 스키마
const imageAssetStyleSchema = baseAssetStyleSchema.extend({
  borderRadius: z.number(),
  keepAspectRatio: z.boolean(),
});

// GIF Asset 스타일 스키마
const gifAssetStyleSchema = baseAssetStyleSchema.extend({
  borderRadius: z.number(),
  keepAspectRatio: z.boolean(),
});

// Text Asset 스타일 스키마
const textAssetStyleSchema = baseAssetStyleSchema.extend({
  text: z.string(),
  color: z.string(),
  align: z.string(),
  fontFamily: z.string(),
  fontSize: z.number(),
  lineHeight: z.number(),
  letterSpacing: z.number(),
  direction: z.string(),
  fontStyle: fontStyleSchema,
  strokeWidth: z.number(),
  strokeColor: z.string(),
  shadow: shadowSchema.optional(),
  background: backgroundSchema.optional(),
  resizeOnEdit: z.boolean().optional(),
  segments: z
    .array(
      z.object({
        text: z.string(),
        color: z.string().optional(),
        fontFamily: z.string().optional(),
        fontWeight: z.string().optional(),
        fontVariant: z.string().optional(),
        fontSize: z.number().optional(),
        opacity: z.number().optional(),
      }),
    )
    .optional(),
});

// Shape(Solid) Asset 스타일 스키마
const shapeAssetStyleSchema = baseAssetStyleSchema.extend({
  fillColor: z.string(),
  strokeWidth: z.number(),
  strokeColor: z.string(),
  borderRadius: z.number(),
  keepAspectRatio: z.boolean(),
  shadow: shadowSchema.optional(),
});

// Blur Asset 스타일 스키마
const blurAssetStyleSchema = baseAssetStyleSchema.extend({
  blurAmount: z.number(),
  borderRadius: z.number(),
  keepAspectRatio: z.boolean(),
});

// Asset 스타일 Union 스키마
const assetStyleSchema = z.union([
  imageAssetStyleSchema,
  gifAssetStyleSchema,
  textAssetStyleSchema,
  shapeAssetStyleSchema,
  blurAssetStyleSchema,
]);

// EditorStarterAsset 타입 체크 함수
const isEditorStarterAsset = (value: unknown): value is EditorStarterAsset => {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;

  return (
    typeof obj.id === 'string' &&
    typeof obj.type === 'string' &&
    ['image', 'video', 'gif', 'audio', 'caption'].includes(
      obj.type as string,
    ) &&
    typeof obj.filename === 'string'
  );
};

const isVideoAsset = (value: unknown): value is VideoAsset => {
  return isEditorStarterAsset(value) && value.type === 'video';
};

const brandTemplateBoundaryVideoSchema = z.object({
  enabled: z.boolean(),
  asset: z.custom<VideoAsset>(isVideoAsset),
});

// 전체 브랜드 템플릿 스키마
export const brandTemplateSchema = z.object({
  // COMMON 템플릿의 비율별 Default 식별값. 표시 이름이나 DB ID에 의존하지 않는다.
  systemDefaultKey: z.literal('original').optional(),
  backgroundColor: z.string(),
  compositionHeight: z.number(),
  compositionWidth: z.number(),
  style: z.object({
    title: textStyleSchema,
    caption: textStyleSchema,
    video: videoStyleSchema,
    assets: z.record(z.string(), assetStyleSchema).default({}), // assetId를 키로 하는 스타일 맵
  }),
  assets: z
    .array(z.custom<EditorStarterAsset>(isEditorStarterAsset))
    .default([]),
  intro: z.string().optional(),
  outro: z.string().optional(),
  introVideo: brandTemplateBoundaryVideoSchema.nullable().optional(),
  outroVideo: brandTemplateBoundaryVideoSchema.nullable().optional(),
  ttsVoice: z
    .object({
      code: z.string(),
      tempo: z.number(),
      pitch: z.number().optional(),
      emotionCode: z.string().optional(),
    })
    .optional(),
  mask: z
    .object({
      enabled: z.boolean(),
      aspectRatio: z.enum(['16:9', '1:1', '9:16', 'custom']).nullable(),
      x: z.number(),
      y: z.number(),
      width: z.number(),
      height: z.number(),
    })
    .optional(),
  hiddenTracks: z
    .object({
      title: z.boolean().optional(),
      caption: z.boolean().optional(),
      video: z.boolean().optional(),
    })
    .optional(),
  trackOrder: z.array(z.string()).optional(),
});

// 타입 추론
export type BrandTemplate = z.infer<typeof brandTemplateSchema>;
export type BrandTemplateBoundaryVideo = z.infer<
  typeof brandTemplateBoundaryVideoSchema
>;
export type TextStyle = z.infer<typeof textStyleSchema>;
export type VideoStyle = z.infer<typeof videoStyleSchema>;
export type Shadow = z.infer<typeof shadowSchema>;
export type Background = z.infer<typeof backgroundSchema>;
export type FontStyle = z.infer<typeof fontStyleSchema>;

// Asset 스타일 타입
export type AssetStyle = z.infer<typeof assetStyleSchema>;
export type ImageAssetStyle = z.infer<typeof imageAssetStyleSchema>;
export type GifAssetStyle = z.infer<typeof gifAssetStyleSchema>;
export type TextAssetStyle = z.infer<typeof textAssetStyleSchema>;
export type ShapeAssetStyle = z.infer<typeof shapeAssetStyleSchema>;
export type BlurAssetStyle = z.infer<typeof blurAssetStyleSchema>;

export const DEFAULT_FONT_FAMILY = 'School Safety Notification' as const;

/**
 * locale/region 별 기본 폰트 선택 구조.
 *
 * ko 는 기존 기본값(School Safety Notification)을 유지한다.
 * en/GLOBAL 용 기본 폰트 정책은 아직 확정되지 않았으므로(제품 결정 필요) 현재는 base 와 동일하게 두되,
 * 확정되면 아래 맵의 en 값만 교체하면 된다 (consumer 는 getDefaultFontFamily(locale) 사용).
 * TODO(font-policy): en/GLOBAL 기본 폰트 확정 시 맵 값 교체.
 */
export const DEFAULT_FONT_FAMILY_BY_LOCALE: Record<string, string> = {
  ko: DEFAULT_FONT_FAMILY,
  en: DEFAULT_FONT_FAMILY,
};

export const getDefaultFontFamily = (locale?: string | null): string =>
  (locale && DEFAULT_FONT_FAMILY_BY_LOCALE[locale]) || DEFAULT_FONT_FAMILY;

// 1:1, 9:16 마스크에서 가로/정사각형 영상일 때 적용할 스케일
export const MASK_VIDEO_SCALE_MAP: Record<'1:1' | '9:16', number> = {
  '1:1': 1.776,
  '9:16': 3.158,
};
